import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../utils";

export type ProviderDataResponse = {
  cardProvider: string;
  accountProvider: string;
};

export type GetEphemeralKeyResponse = {
  ephemeralKey: string;
};

export interface ViewPinCodeState {
  getProviderDataStatus: QueryStatus;
  getEphemeralKeyStatus: QueryStatus;
  cardProvider: string | null;
  accountProvider: string | null;
  nonce: string | null;
  ephemeralKey: string | null;
}

const initialState: ViewPinCodeState = {
  getProviderDataStatus: "idle",
  getEphemeralKeyStatus: "idle",
  cardProvider: null,
  accountProvider: null,
  nonce: null,
  ephemeralKey: null,
};

export const getCardProviderDataAsync = createAsyncThunk(
  "viewPinCode/getProviderData",
  async (payload: { cardUuid: string }) => {
    const axios = authAxios();
    const response = await axios.get<ProviderDataResponse>(
      `card/${payload.cardUuid}/provider_data`,
    );
    return response.data;
  },
);

export const getCardPinEphemeralKeyAsync = createAsyncThunk(
  "viewPinCode/getEphemeralKey",
  async (payload: { cardUuid: string; nonce: string }) => {
    const axios = authAxios();
    const response = await axios.post<GetEphemeralKeyResponse>(
      `card/${payload.cardUuid}/pin/ephemeral_key`,
      { nonce: payload.nonce },
    );
    return response.data;
  },
);

export const viewPinCodeSlice = createSlice({
  name: "viewPinCode",
  initialState,
  reducers: {
    resetAll(state) {
      state.getProviderDataStatus = "idle";
      state.getEphemeralKeyStatus = "idle";
      state.cardProvider = null;
      state.accountProvider = null;
      state.nonce = null;
      state.ephemeralKey = null;
    },
    resetNonce(state) {
      state.nonce = null;
    },
    storeNonce(state, action: PayloadAction<{ nonce: string }>) {
      state.nonce = action.payload.nonce;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCardProviderDataAsync.pending, (state) => {
        state.getProviderDataStatus = "processing";
      })
      .addCase(getCardProviderDataAsync.fulfilled, (state, action) => {
        state.getProviderDataStatus = "success";
        state.cardProvider = action.payload.cardProvider;
        state.accountProvider = action.payload.accountProvider;
      })
      .addCase(getCardProviderDataAsync.rejected, (state) => {
        state.getProviderDataStatus = "failed";
      })
      .addCase(getCardPinEphemeralKeyAsync.pending, (state) => {
        state.getEphemeralKeyStatus = "processing";
      })
      .addCase(getCardPinEphemeralKeyAsync.fulfilled, (state, action) => {
        state.getEphemeralKeyStatus = "success";
        state.ephemeralKey = action.payload.ephemeralKey;
      })
      .addCase(getCardPinEphemeralKeyAsync.rejected, (state) => {
        state.getEphemeralKeyStatus = "failed";
      });
  },
});

export default viewPinCodeSlice.reducer;
